import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, Search, Image as ImageIcon, RefreshCw } from 'lucide-react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { formatDate } from '../utils/dateFormat';
import { formatUiCurrencyCompact } from '../utils/uiFormat';

export default function MediaManagement({ embedded = false }) {
  const [tab, setTab] = useState('pos_icons');
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const { hasPermission } = useAuthStore();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catRes, menuRes, expRes] = await Promise.all([
        api.get('/categories/'),
        api.get('/menu-items/?page_size=500&active=true'),
        api.get('/expenses/?page_size=500'),
      ]);
      setCategories(catRes.data.results || catRes.data);
      setMenuItems(menuRes.data.results || menuRes.data);
      const allExp = expRes.data.results || expRes.data;
      setExpenses(allExp.filter(e => e.invoice_image));
    } catch {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryImage = async (cat, file) => {
    if (!file) return;
    setUploadingId(`cat-${cat.id}`);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await api.patch(`/categories/${cat.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`"${cat.name}" icon updated`);
      fetchAll();
    } catch {
      toast.error('Failed to update category image');
    } finally {
      setUploadingId(null);
    }
  };

  const handleMenuItemImage = async (item, file) => {
    if (!file) return;
    setUploadingId(`item-${item.id}`);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await api.patch(`/menu-items/${item.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`"${item.name}" image updated`);
      fetchAll();
    } catch {
      toast.error('Failed to update item image');
    } finally {
      setUploadingId(null);
    }
  };

  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );
  const filteredMenuItems = menuItems.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    (m.category_name || '').toLowerCase().includes(search.toLowerCase())
  );
  const filteredExpenses = expenses.filter(e =>
    (e.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.vendor_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.expense_type || '').toLowerCase().includes(search.toLowerCase())
  );

  const ImageCard = ({ src, name, sub, id, onUpload, canEdit }) => (
    <div className="border rounded-xl p-3 flex flex-col items-center gap-2 bg-white hover:shadow-md transition">
      <div
        className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center cursor-pointer"
        onClick={() => src && setSelectedImage(src)}
      >
        {src ? (
          <img src={src} alt={name} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon size={28} className="text-gray-300" />
        )}
      </div>
      <div className="text-center w-full">
        <p className="text-xs font-medium text-gray-700 truncate">{name}</p>
        {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
      </div>
      {canEdit && (
        <label className={`text-xs px-2 py-1 rounded cursor-pointer flex items-center gap-1 transition ${uploadingId === id ? 'bg-gray-100 text-gray-400' : 'text-blue-600 bg-blue-50 hover:bg-blue-100'}`}>
          {uploadingId === id ? 'Uploading…' : src ? 'Change' : 'Upload'}
          <input type="file" accept="image/*" className="hidden" disabled={uploadingId !== null} onChange={e => onUpload(e.target.files[0])} />
        </label>
      )}
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {embedded ? (
        <div className="flex justify-end">
          <button onClick={fetchAll} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Media Manager</h1>
            <p className="text-sm text-gray-500">Manage POS icons and expense receipt images</p>
          </div>
          <button onClick={fetchAll} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-xl shadow-sm p-1 overflow-x-auto">
        {[
          { id: 'pos_icons', label: `POS Icons (${categories.length + menuItems.length})` },
          { id: 'receipts', label: `Receipt Images (${expenses.length})` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition flex-shrink-0 ${tab === t.id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={tab === 'pos_icons' ? 'Search categories or items...' : 'Search receipts...'}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto" />
          <p className="text-gray-500 mt-3 text-sm">Loading media…</p>
        </div>
      ) : tab === 'pos_icons' ? (
        <div className="space-y-5">
          {/* Category Icons */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              Category Icons
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{filteredCategories.length}</span>
              <span className="text-xs text-gray-400 font-normal">— shown on POS category bar</span>
            </h2>
            {filteredCategories.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-6">No categories found</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {filteredCategories.map(cat => (
                  <ImageCard
                    key={cat.id}
                    src={cat.image}
                    name={cat.name}
                    sub={cat.department_name}
                    id={`cat-${cat.id}`}
                    canEdit={hasPermission('menu', 'edit')}
                    onUpload={file => handleCategoryImage(cat, file)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Menu Item Images */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              Menu Item Images
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{filteredMenuItems.length}</span>
              <span className="text-xs text-gray-400 font-normal">— shown on POS item cards</span>
            </h2>
            {filteredMenuItems.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-6">No items found</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {filteredMenuItems.map(item => (
                  <ImageCard
                    key={item.id}
                    src={item.image}
                    name={item.name}
                    sub={item.category_name}
                    id={`item-${item.id}`}
                    canEdit={hasPermission('menu', 'edit')}
                    onUpload={file => handleMenuItemImage(item, file)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Receipts Tab */
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            Expense Receipts
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{filteredExpenses.length}</span>
            <span className="text-xs text-gray-400 font-normal">— click any image to enlarge</span>
          </h2>
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <ImageIcon size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No receipt images found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filteredExpenses.map(exp => (
                <div
                  key={exp.id}
                  className="border rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition group"
                  onClick={() => setSelectedImage(exp.invoice_image)}
                >
                  <div className="h-36 bg-gray-100 overflow-hidden">
                    <img
                      src={exp.invoice_image}
                      alt="Receipt"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="p-2 space-y-0.5">
                    <p className="text-xs font-medium text-gray-700 truncate">{exp.description || exp.expense_type || '—'}</p>
                    {exp.vendor_name && <p className="text-xs text-gray-400 truncate">{exp.vendor_name}</p>}
                    <p className="text-xs text-gray-400">{formatDate(exp.date)}</p>
                    <p className="text-xs font-semibold text-primary-600">{formatUiCurrencyCompact(exp.amount || 0)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black/40 rounded-full p-1"
            onClick={() => setSelectedImage(null)}
          >
            <X size={24} />
          </button>
          <img
            src={selectedImage}
            alt=""
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
